import { html, css, LitElement } from "lit";
import { customElement, state } from "lit/decorators.js";
import { executionsStore } from "../executions-store.ts";
import type { ProjectExecute } from "../../types/ead-types.ts";
import { isLiveProjectExecute } from "../run-status.ts";
import { isRunChatSessionLive, resolveRunSessionKey } from "../run-session-key.ts";
import "./../components/chat-panel.ts";
import "./runs.ts";

type ClosedRightTab = "report" | "history";

@customElement("ead-closed-runs-view")
export class EadClosedRunsView extends LitElement {
  static styles = css`
    :host { display: block; height: 100%; min-height: 0; }
    .layout { display: grid; grid-template-columns: 330px 1fr; height: 100%; min-height: 0; }
    .list-pane { border-right: 1px solid color-mix(in srgb, var(--border) 74%, transparent); display: flex; flex-direction: column; min-height: 0; }
    .list-head { padding: 12px; border-bottom: 1px solid color-mix(in srgb, var(--border) 74%, transparent); display: grid; gap: 8px; }
    .list-title { font-size: 13px; font-weight: 600; color: var(--text-strong, #f4f4f5); }
    .search { width: 100%; box-sizing: border-box; border-radius: 10px; border: 1px solid color-mix(in srgb, var(--border) 72%, transparent); background: color-mix(in srgb, var(--bg-elevated) 82%, transparent); color: var(--text); padding: 8px 10px; font-size: 12px; }
    .pager { display: flex; justify-content: space-between; align-items: center; font-size: 11px; color: var(--muted, #8b8d93); }
    .pager button { border: 1px solid color-mix(in srgb, var(--border) 72%, transparent); background: transparent; color: var(--text); border-radius: 8px; padding: 2px 8px; cursor: pointer; }
    .pager button:disabled { opacity: .45; cursor: default; }
    .groups { overflow-y: auto; padding: 10px; display: grid; gap: 8px; min-height: 0; }
    .run-btn { width: 100%; text-align: left; border: 1px solid color-mix(in srgb, var(--border) 70%, transparent); border-radius: 10px; background: color-mix(in srgb, var(--bg-elevated) 88%, transparent); color: var(--text); padding: 8px 10px; cursor: pointer; }
    .run-btn:hover { background: color-mix(in srgb, var(--bg-hover, #232733) 80%, transparent); }
    .run-btn--selected { background: color-mix(in srgb, var(--accent, #ff5c5c) 10%, transparent); }
    .run-name { font-size: 12px; font-weight: 600; }
    .run-meta { font-size: 11px; color: var(--muted, #8b8d93); margin-top: 2px; white-space: nowrap; text-overflow: ellipsis; overflow: hidden; }
    .right-pane { display: flex; flex-direction: column; min-height: 0; }
    .tabs { display: flex; gap: 8px; padding: 10px 12px; border-bottom: 1px solid color-mix(in srgb, var(--border) 74%, transparent); }
    .tab-btn { border: 1px solid color-mix(in srgb, var(--border) 72%, transparent); background: transparent; color: var(--text); border-radius: 8px; font-size: 12px; padding: 6px 10px; cursor: pointer; }
    .tab-btn--active { background: color-mix(in srgb, var(--accent, #ff5c5c) 14%, transparent); border-color: color-mix(in srgb, var(--accent, #ff5c5c) 42%, transparent); }
    .panel { flex: 1; min-height: 0; overflow: auto; padding: 12px; }
    .panel--interactive { padding: 0; overflow: hidden; }
    .section-title { font-size: 13px; font-weight: 600; margin-bottom: 8px; }
    .hint { color: var(--muted, #8b8d93); font-size: 12px; }
  `;

  @state() private _search = "";
  @state() private _page = 0;
  @state() private _selectedRunId = "";
  @state() private _rightTab: ClosedRightTab = "report";
  @state() private _templateNames: Record<string, string> = {};
  private _unsubscribeStore: (() => void) | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    this._unsubscribeStore = executionsStore.subscribe(() => this.requestUpdate());
    void this._loadTemplateNames();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this._unsubscribeStore?.();
    this._unsubscribeStore = null;
  }

  private async _loadTemplateNames(): Promise<void> {
    const result = await hermesClient.request<{ templates?: Array<{ id?: string; name?: string }> }>(
      "templates.list",
    );
    const rows = Array.isArray(result?.templates) ? result.templates : [];
    const map: Record<string, string> = {};
    for (const row of rows) {
      const id = String(row?.id || "").trim();
      const name = String(row?.name || "").trim();
      if (id && name) map[id] = name;
    }
    this._templateNames = map;
  }

  private _isClosed(run: ProjectExecute): boolean {
    if (typeof run.chatDeliveryActive === "boolean") return !run.chatDeliveryActive;
    const activity = String(run.reportingActivityStatus || "").trim().toLowerCase();
    if (activity === "closed") return true;
    if (activity === "active") return false;
    return !isLiveProjectExecute(run);
  }

  private _closedRuns(): ProjectExecute[] {
    return executionsStore.executions
      .filter((run) => this._isClosed(run))
      .sort((a, b) => Number(b.startTime || 0) - Number(a.startTime || 0));
  }

  private _displayRunTitle(run: ProjectExecute): string {
    const baseName = String(run.name || "").replace(/^Run:\s*/i, "").trim() || "Untitled Run";
    const runNumber = Number(run.runNumber || 0);
    return runNumber > 0 ? `Run ${runNumber}, ${baseName}` : baseName;
  }

  private _templateName(run: ProjectExecute): string {
    const id = String(run.linkedTemplateId || "").trim();
    return String(this._templateNames[id] || "").trim() || "Unnamed Template";
  }

  private _filteredClosedRuns(): ProjectExecute[] {
    const q = this._search.trim().toLowerCase();
    const filtered: ProjectExecute[] = [];
    for (const run of this._closedRuns()) {
      const templateName = this._templateName(run);
      const haystack = [
        templateName,
        run.name,
        run.targetUrl,
        run.description,
        this._displayRunTitle(run),
      ].join(" ").toLowerCase();
      if (q && !haystack.includes(q)) continue;
      filtered.push(run);
    }
    return filtered;
  }

  private _selectedRun(runs: ProjectExecute[]): ProjectExecute | null {
    if (!runs.length) return null;
    const selected = runs.find((run) => run.id === this._selectedRunId) || runs[0];
    if (selected.id !== this._selectedRunId) this._selectedRunId = selected.id;
    return selected;
  }

  private _ensureExecutionSelected(run: ProjectExecute | null): void {
    if (!run?.id) return;
    if (executionsStore.selectedExecutionId === run.id) return;
    executionsStore.setSelectedExecutionId(run.id);
  }

  render() {
    if (executionsStore.loading && executionsStore.executions.length === 0) {
      return html`<div class="hint" style="padding:16px;">Loading closed projects…</div>`;
    }
    const filteredRuns = this._filteredClosedRuns();
    const pageSize = 25;
    const pageCount = Math.max(1, Math.ceil(filteredRuns.length / pageSize));
    const safePage = Math.max(0, Math.min(this._page, pageCount - 1));
    const pageRuns = filteredRuns.slice(safePage * pageSize, safePage * pageSize + pageSize);
    const selected = this._selectedRun(filteredRuns);
    this._ensureExecutionSelected(selected);

    return html`
      <div class="layout">
        <div class="list-pane">
          <div class="list-head">
            <div class="list-title">Completed Project Run</div>
            <input
              class="search"
              type="search"
              placeholder="Search closed project run"
              .value=${this._search}
              @input=${(event: Event) => {
                this._search = (event.target as HTMLInputElement).value || "";
                this._page = 0;
              }}
            />
            <div class="pager">
              <button type="button" ?disabled=${safePage <= 0} @click=${() => { this._page = safePage - 1; }}>◀</button>
              <span>${safePage + 1}/${pageCount}</span>
              <button type="button" ?disabled=${safePage >= pageCount - 1} @click=${() => { this._page = safePage + 1; }}>▶</button>
            </div>
          </div>
          <div class="groups">
            ${pageRuns.length === 0
              ? html`<div class="hint">No closed runs found.</div>`
              : pageRuns.map((run) => html`
                  <button
                    class="run-btn ${selected?.id === run.id ? "run-btn--selected" : ""}"
                    type="button"
                    @click=${() => {
                      this._selectedRunId = run.id;
                      this._ensureExecutionSelected(run);
                    }}
                  >
                    <div class="run-name">${this._displayRunTitle(run)}</div>
                    <div class="run-meta">${this._templateName(run)} · ${run.targetUrl || "No target"}</div>
                  </button>
                `)}
          </div>
        </div>

        <div class="right-pane">
          <div class="tabs">
            <button
              class="tab-btn ${this._rightTab === "report" ? "tab-btn--active" : ""}"
              type="button"
              @click=${() => { this._rightTab = "report"; }}
            >
              Project Interactive
            </button>
            <button
              class="tab-btn ${this._rightTab === "history" ? "tab-btn--active" : ""}"
              type="button"
              @click=${() => { this._rightTab = "history"; }}
            >
              AI Chat History
            </button>
          </div>
          <div class="panel ${this._rightTab === "report" ? "panel--interactive" : ""}">
            ${!selected
              ? html`<div class="hint">Select a closed run to view details.</div>`
              : this._rightTab === "history"
                ? html`
                    <ead-chat-panel
                      .config=${{
                        sessionKey: resolveRunSessionKey(selected),
                        sessionKeySource: isRunChatSessionLive(selected) ? "live" : "fallback",
                        executionId: selected.id,
                        title: `AI Chat History · ${this._displayRunTitle(selected)}`,
                        readonly: true,
                        showTimestamps: true,
                        showAvatars: true,
                        pollIntervalMs: 15000,
                        statusPollIntervalMs: 15000,
                        enableStatusPolling: false,
                      }}
                    ></ead-chat-panel>
                  `
                : html`<ead-runs-view .forceReadonlyChat=${true} .hideChatPanel=${true}></ead-runs-view>`}
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "ead-closed-runs-view": EadClosedRunsView;
  }
}

